import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { captureError, Severity } from '@chegg/observability';
import isWindowDefined from '../isWindowDefined';
import { CWF_TEAM_NAME } from './constants';
// Summarize the state of local storage for diagnostic use.
// Note that this calculates the approximate size of the Storage object
// by summing the length of each key and value.
// This is NOT exact, and does NOT equate to raw bytes stored.
// It is intended to guide developers toward problematic storage keys.
export function snapshotStorage(store) {
  var sizesByKey = {};
  var totalSize = 0;
  for (var i = 0; i < store.length; ++i) {
    var _key = store.key(i);
    if (_key) {
      var _value$length;
      var value = store.getItem(_key);
      var keySize = _key.length;
      var valueSize = (_value$length = value === null || value === void 0 ? void 0 : value.length) !== null && _value$length !== void 0 ? _value$length : 0;
      var itemSize = keySize + valueSize;
      sizesByKey[_key] = itemSize;
      totalSize += itemSize;
    }
  }
  return {
    sizesByKey: sizesByKey,
    totalSize: totalSize
  };
}

/**
 *
 * Removes an item from local storage by key.
 */
var removeItem = function removeItem(key) {
  if (isWindowDefined) {
    window.localStorage.removeItem(key);
  }
};

// gets an item from local storage by key
// we extend and overload the getItem method to define a default return value if no data exist.
function getItem(key, otherwise) {
  var data = isWindowDefined ? window.localStorage.getItem(key) : null;
  if (data !== null) {
    try {
      return JSON.parse(data);
    } catch (error) {
      captureError({
        error: error,
        location: "LocalStorageUtils.getItem | key=".concat(key),
        // key can be used to debug in case of any error
        severity: Severity.ERROR
      }, {
        teamName: CWF_TEAM_NAME
      });
      removeItem(key);
    }
  }
  // If the default argument 'otherwise' is passed, we expect the same type as the return value.
  // Otherwise, the result could be nullable.
  if (otherwise) {
    return otherwise;
  }
  return null;
}

// Service that implements set, get, remove operations with
// localStorage and enhances these methods with error handling
export var LocalStorageUtils = {
  // sets an item in local storage by key and value.
  // logs to new relic in case of an error
  setItem: function setItem(key, value) {
    if (isWindowDefined) {
      var newValue = undefined;
      try {
        newValue = JSON.stringify(value);
        window.localStorage.setItem(key, newValue);
      } catch (error) {
        // If newValue can't be stringified, use a descriptive string value.
        var valueSize = newValue === undefined ? 'unable to serialize value' : key.length + newValue.length;

        // We don't wrap this `JSON.stringify()` in a try{} because it should
        // only contain existing storage keys (strings) and sizes (numbers).
        var storageData = _objectSpread(_objectSpread({}, snapshotStorage(window.localStorage)), {}, {
          newEntry: _defineProperty({}, key, valueSize)
        });
        captureError({
          error: error,
          location: 'LocalStorageUtils.setItem',
          severity: Severity.ERROR
        }, {
          storageData: JSON.stringify(storageData),
          teamName: CWF_TEAM_NAME
        });
        return undefined;
      }
    }
  },
  getItem: getItem,
  removeItem: removeItem,
  snapshotStorage: snapshotStorage
};